import React, { useState, useEffect, useMemo } from 'react';
import { useLanguage } from '../../../context/LanguageContext';
import { useCollection } from '../../../hooks/useCollection';

export default function HistoryPage({ settings }) {
  const { lang, t } = useLanguage();
  const isAr = lang === 'ar';
  
  const { data: rentals, loading: loadingRentals } = useCollection('rentals');
  const { data: cars, loading: loadingCars } = useCollection('cars');
  const [selectedCar, setSelectedCar] = useState('');

  const filtered = useMemo(() => {
    return selectedCar ? rentals.filter(r => r.carId === selectedCar) : rentals;
  }, [selectedCar, rentals]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => (b.startDate || '').localeCompare(a.startDate || ''));
  }, [filtered]);

  const carStats = useMemo(() => {
    if (!selectedCar) return null;
    const c = rentals.filter(r => r.carId === selectedCar);
    return {
      total: c.length,
      revenue: c.reduce((s, r) => s + (parseFloat(r.totalAmount) || 0), 0),
      days: c.reduce((s, r) => s + (parseInt(r.days || r.totalDays) || 0), 0)
    };
  }, [selectedCar, rentals]);

  const historyHeaders = [
    { key: 'from', l: t('col_from_history') },
    { key: 'client', l: t('col_client') },
    { key: 'car', l: t('col_car') },
    { key: 'days', l: t('col_days_history') },
    { key: 'amount', l: isAr ? 'المبلغ الإجمالي' : 'Montant' },
    { key: 'status', l: t('col_status') }
  ];

  if (loadingRentals || loadingCars) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">{t('history_title')}</h1>
      
      {/* Selection Filter */}
      <div className="mb-6">
        <select 
          value={selectedCar} 
          onChange={e => setSelectedCar(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg text-base bg-white"
        >
          <option value="">{t('all_cars_option')}</option>
          {cars.map(c => (
            <option key={c.id} value={c.id}>
              {`${c.brand} ${c.model} (${c.plate})`}
            </option>
          ))}
        </select>
      </div>
      
      {/* Car Stats Card */}
      {carStats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
            <p className="text-sm text-gray-500 font-medium">{t('rentals_number')}</p>
            <p className="text-2xl font-black text-gray-900 mt-1">{carStats.total}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
            <p className="text-sm text-gray-500 font-medium">{t('rented_days')}</p>
            <p className="text-2xl font-black text-gray-900 mt-1">{carStats.days}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
            <p className="text-sm text-gray-500 font-medium">{t('total_revenue_label')}</p>
            <p className="text-2xl font-black text-green-600 mt-1">
              {carStats.revenue.toFixed(0)} {settings.currency || 'MAD'}
            </p>
          </div>
        </div>
      )}
      
      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {historyHeaders.map(t => (
                  <th 
                    key={t.key} 
                    className={`${isAr ? 'text-right' : 'text-left'} px-6 py-4 font-bold text-gray-700 uppercase tracking-wider`}
                  >
                    {t.l}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sorted.map(r => (
                <tr key={r.id} className="hover:bg-gray-50/50 transition-colors">
                  <td className={`px-6 py-4 text-gray-600 ${isAr ? 'text-right' : 'text-left'}`}>
                    {r.startDate}
                  </td>
                  <td className={`px-6 py-4 font-medium text-gray-900 ${isAr ? 'text-right' : 'text-left'}`}>
                    {r.clientName}
                  </td>
                  <td className={`px-6 py-4 text-gray-650 text-gray-600 ${isAr ? 'text-right' : 'text-left'}`}>
                    {r.carName}
                  </td>
                  <td className={`px-6 py-4 text-gray-600 ${isAr ? 'text-right' : 'text-left'}`}>
                    {r.days || r.totalDays || 0}
                  </td>
                  <td className={`px-6 py-4 font-bold text-gray-900 ${isAr ? 'text-right' : 'text-left'}`}>
                    {r.totalAmount} {settings.currency || 'MAD'}
                  </td>
                  <td className={`px-6 py-4 ${isAr ? 'text-right' : 'text-left'}`}>
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold ${
                      r.status === 'active' ? 'bg-green-100 text-green-700' :
                      r.status === 'completed' ? 'bg-gray-100 text-gray-700' : 
                      'bg-red-100 text-red-700'
                    }`}>
                      {r.status === 'active' ? t('status_active') : r.status === 'completed' ? t('status_completed') : t('status_cancelled')}
                    </span>
                  </td>
                </tr>
              ))}
              {sorted.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center py-8 text-gray-550 text-gray-500 italic">
                    {t('no_invoices_found')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
